import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Card, CardContent, Grid, Typography } from '@mui/material';
import {
  Inventory2 as InventoryIcon,
  ShoppingCart as OrdersIcon,
  Receipt as InvoicesIcon,
  CheckCircle as CompletedIcon,
  CurrencyRupee as WorthIcon,
  Inbox as ReceivedIcon,
} from '@mui/icons-material';
import { useAuthStore } from '@/stores/authStore';
import { ordersApi } from '@/api/orders';
import { listProducts } from '@/api/products';
import { PageHeader } from '@/components';
import type { Order } from '@/types/order';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isSameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
}


function fmtAmount(n: number): string {
  if (n >= 10000000) return `₹${(n / 10000000).toLocaleString('en-IN', { maximumFractionDigits: 1 })}Cr`;
  if (n >= 100000) return `₹${(n / 100000).toLocaleString('en-IN', { maximumFractionDigits: 1 })}L`;
  if (n >= 1000) return `₹${(n / 1000).toLocaleString('en-IN', { maximumFractionDigits: 1 })}K`;
  return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

function fmtAmountLabel(n: number): string {
  if (n >= 10000000) return `₹ ${(n / 10000000).toLocaleString('en-IN', { maximumFractionDigits: 2 })} Cr`;
  if (n >= 100000) return `₹ ${(n / 100000).toLocaleString('en-IN', { maximumFractionDigits: 2 })} L`;
  if (n >= 1000) return `₹ ${(n / 1000).toLocaleString('en-IN', { maximumFractionDigits: 2 })} K`;
  return `₹ ${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

const DONE_STATUSES = ['DELIVERED', 'INVOICED', 'CANCELLED', 'REJECTED', 'DRAFT'];

function mrpWorthOf(orders: Order[]): number {
  return orders
    .filter(o => !DONE_STATUSES.includes(o.status))
    .reduce((sum, o) =>
      sum + (o.items ?? []).reduce((s, i) => {
        if (i.mrp == null) return s;
        return s + Number(i.mrp) * Number(i.qty);
      }, 0), 0);
}

function ptrWorthOf(orders: Order[]): number {
  return orders
    .filter(o => !DONE_STATUSES.includes(o.status))
    .reduce((sum, o) =>
      sum + (o.items ?? []).reduce((s, i) =>
        s + Number(i.unitPrice) * Number(i.qty), 0), 0);
}

function orderTotalAmount(order: Order): number {
  return (order.items ?? []).reduce((s, i) => s + Number(i.unitPrice) * Number(i.qty), 0);
}

// ---------------------------------------------------------------------------
// SVG Line Chart
// ---------------------------------------------------------------------------

interface ChartPoint { label: string; value: number }

// CSS height is fixed at CHART_CSS_H — both monthly and daily use the same value,
// so the SVG y-scale is identical regardless of column width, keeping x-axes aligned.
const CHART_CSS_H = 220;

function LineChart({ data, color, verticalLabels = false }: { data: ChartPoint[]; color: string; verticalLabels?: boolean }) {
  // VH matches CSS height exactly → y-scale = 1.0, no vertical distortion.
  // VW is wide enough that x-scale stays close to 1 at typical column widths.
  const VW = 700, VH = CHART_CSS_H;
  const padL = 46, padR = 12, padT = 28;
  const padB = verticalLabels ? 52 : 30;
  const chartW = VW - padL - padR;
  const chartH = VH - padT - padB;

  const maxV = Math.max(...data.map(d => d.value), 1);
  const toX = (i: number) => padL + (i / Math.max(data.length - 1, 1)) * chartW;
  const toY = (v: number) => padT + chartH - (v / maxV) * chartH;

  const points = data.map((d, i) => ({ x: toX(i), y: toY(d.value), ...d }));
  const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  const areaD = `${pathD} L${points[points.length - 1].x.toFixed(1)},${padT + chartH} L${points[0].x.toFixed(1)},${padT + chartH} Z`;
  const gradId = `lg-${color.replace('#', '')}`;

  const xStep = verticalLabels ? 1 : Math.max(1, Math.floor(data.length / 7));
  const top6idx = new Set(
    points.map((p, i) => ({ i, v: p.value })).filter(x => x.v > 0)
      .sort((a, b) => b.v - a.v).slice(0, 6).map(x => x.i)
  );
  const yTicks = [0.25, 0.5, 0.75, 1].map(r => ({ r, v: maxV * r, y: toY(maxV * r) }));

  return (
    <svg
      viewBox={`0 0 ${VW} ${VH}`}
      preserveAspectRatio="none"
      width="100%"
      height={CHART_CSS_H}
      style={{ display: 'block', fontFamily: 'inherit' }}
    >
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.22" />
          <stop offset="100%" stopColor={color} stopOpacity="0.02" />
        </linearGradient>
      </defs>
      {yTicks.map(t => (
        <g key={t.r}>
          <line x1={padL} y1={t.y} x2={VW - padR} y2={t.y}
            stroke="#e5e7eb" strokeWidth="1" strokeDasharray="4,3" />
          <text x={padL - 5} y={t.y + 4} textAnchor="end" fontSize="10" fill="#9ca3af" fontFamily="inherit">
            {fmtAmountLabel(t.v)}
          </text>
        </g>
      ))}
      <path d={areaD} fill={`url(#${gradId})`} />
      <path d={pathD} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
      {points.map((p, i) => (
        <g key={i}>
          <circle cx={p.x} cy={p.y} r={top6idx.has(i) ? 4 : 2.5}
            fill={top6idx.has(i) ? color : '#fff'} stroke={color} strokeWidth="1.5" />
          {top6idx.has(i) && (() => {
            const labelY = Math.max(p.y - 10, padT - 4);
            // Edge-aware anchor: first point → start, last point → end, others → middle
            const anchor = i === 0 ? 'start' : i === data.length - 1 ? 'end' : 'middle';
            return (
              <text
                x={p.x}
                y={labelY}
                textAnchor={anchor}
                fontSize="12"
                fontFamily="inherit"
                fill={color}
                stroke="white"
                strokeWidth="3"
                paintOrder="stroke"
              >
                {fmtAmountLabel(p.value)}
              </text>
            );
          })()}
          {i % xStep === 0 && (
            verticalLabels ? (
              // translate to anchor point first, then rotate — reliable with any scale
              <text
                transform={`translate(${p.x}, ${padT + chartH + 6}) rotate(-90)`}
                textAnchor="end"
                fontSize="11"
                fill="#6b7280"
                fontFamily="inherit"
              >
                {p.label}
              </text>
            ) : (
              <text
                x={p.x} y={padT + chartH + 18}
                textAnchor="middle" fontSize="11"
                fill="#6b7280" fontFamily="inherit"
              >
                {p.label}
              </text>
            )
          )}
        </g>
      ))}
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Revenue Cards
// ---------------------------------------------------------------------------

const CARD_HEIGHT = 80;

interface TotalCardProps { label: string; value: string; accent: string }

function TotalCard({ label, value, accent }: TotalCardProps) {
  return (
    <Card sx={{
      borderRadius: 2,
      boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
      border: `1.5px solid ${accent}22`,
      height: CARD_HEIGHT,
      display: 'flex',
      alignItems: 'center',
    }}>
      <CardContent sx={{ p: '10px 14px !important', width: '100%' }}>
        <Typography sx={{ fontSize: 20, fontWeight: 800, color: accent, letterSpacing: '-0.5px', lineHeight: 1.1 }}>
          {value}
        </Typography>
        <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#6b7280', letterSpacing: '0.08em', textTransform: 'uppercase', mt: 0.4 }}>
          {label}
        </Typography>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// DashboardPage
// ---------------------------------------------------------------------------

export function DashboardPage() {
  const { user } = useAuthStore();
  const navigate = useNavigate();

  const isDistributor = user?.tenantType === 'WHOLESALER';
  const isRetailerOrRep =
    user?.tenantType === 'RETAILER' || user?.tenantType === 'REPRESENTATIVE';

  // Stat card values
  const [products, setProducts] = useState('—');
  const [receivedOrders, setReceivedOrders] = useState('—');
  const [distPending, setDistPending] = useState('—');
  const [distWorth, setDistWorth] = useState('—');

  const [placedOrders, setPlacedOrders] = useState('—');
  const [pendingInvoices, setPendingInvoices] = useState('—');
  const [completedOrders, setCompletedOrders] = useState('—');
  const [ordersWorth, setOrdersWorth] = useState('—');

  // Chart data
  const [allOrders, setAllOrders] = useState<Order[]>([]);

  useEffect(() => {
    if (!user) return;

    if (isDistributor) {
      Promise.allSettled([
        listProducts(0, 1),
        ordersApi.list({ distributorId: user.tenantId, size: 1000 }),
      ]).then(([prodRes, ordRes]) => {
        if (prodRes.status === 'fulfilled')
          setProducts(String(prodRes.value.totalElements));

        if (ordRes.status === 'fulfilled') {
          const orders: Order[] = ordRes.value.content ?? [];
          setAllOrders(orders);
          setReceivedOrders(String(
            orders.filter(o => o.status === 'PLACED' || o.status === 'VIEWED').length
          ));
          setDistPending(String(
            orders.filter(o => o.status === 'CONFIRMED').length
          ));
          setDistWorth(fmtAmount(mrpWorthOf(orders)));
        }
      });
    } else if (isRetailerOrRep) {
      ordersApi.list({ retailerId: user.tenantId, size: 1000 }).then(res => {
        const orders: Order[] = res.content ?? [];
        setAllOrders(orders);
        setPlacedOrders(String(orders.length));
        setPendingInvoices(String(
          orders.filter(o => ['PLACED', 'VIEWED', 'CONFIRMED', 'PARTIALLY_FULFILLED'].includes(o.status)).length
        ));
        setCompletedOrders(String(
          orders.filter(o => o.status === 'DELIVERED' || o.status === 'INVOICED').length
        ));
        setOrdersWorth(fmtAmount(ptrWorthOf(orders)));
      }).catch(() => {});
    }
  }, [user, isDistributor, isRetailerOrRep]);

  // Derived totals from allOrders
  const { todayTotal, yesterdayTotal, weekTotal, monthTotal } = useMemo(() => {
    const now = new Date();
    const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
    // Week start = last Monday (or today if Monday)
    const weekStart = new Date(now);
    const day = now.getDay(); // 0=Sun,1=Mon,...
    weekStart.setDate(now.getDate() - ((day === 0 ? 7 : day) - 1));
    weekStart.setHours(0, 0, 0, 0);
    // Month start
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    let todayTotal = 0, yesterdayTotal = 0, weekTotal = 0, monthTotal = 0;
    for (const o of allOrders) {
      if (!o.orderDate) continue;
      const d = new Date(o.orderDate);
      const amt = orderTotalAmount(o);
      if (isSameDay(d, now)) todayTotal += amt;
      if (isSameDay(d, yesterday)) yesterdayTotal += amt;
      if (d >= weekStart) weekTotal += amt;
      if (d >= monthStart) monthTotal += amt;
    }
    return { todayTotal, yesterdayTotal, weekTotal, monthTotal };
  }, [allOrders]);

  // Monthly totals — last 6 months
  const monthlyData = useMemo((): ChartPoint[] => {
    const now = new Date();
    const buckets: Record<string, number> = {};
    const labels: string[] = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${d.getMonth()}`;
      const label = d.toLocaleString('en-IN', { month: 'short' });
      buckets[key] = 0;
      labels.push(label);
    }
    for (const o of allOrders) {
      if (!o.orderDate) continue;
      const d = new Date(o.orderDate);
      const key = `${d.getFullYear()}-${d.getMonth()}`;
      if (key in buckets) buckets[key] += orderTotalAmount(o);
    }
    return Object.entries(buckets).map(([, v], i) => ({ label: labels[i], value: v }));
  }, [allOrders]);

  // Daily totals — last 14 days including today
  const dailyData = useMemo((): ChartPoint[] => {
    const now = new Date();
    const result: ChartPoint[] = [];
    for (let i = 13; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() - i);
      result.push({ label: String(d.getDate()), value: 0 });
    }
    const todayMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    for (const o of allOrders) {
      if (!o.orderDate) continue;
      const d = new Date(o.orderDate);
      const dayMidnight = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
      const diff = Math.floor((todayMidnight - dayMidnight) / 86400000);
      if (diff >= 0 && diff < 14) {
        result[13 - diff].value += orderTotalAmount(o);
      }
    }
    return result;
  }, [allOrders]);

  const distributorStats = [
    { label: 'Products', value: products, icon: <InventoryIcon />, color: '#1565c0', onClick: () => navigate('/products') },
    { label: 'Order Inbox', value: receivedOrders, icon: <ReceivedIcon />, color: '#00897b', onClick: () => navigate('/orders/inbox') },
    { label: 'Pending Invoices', value: distPending, icon: <InvoicesIcon />, color: '#ef6c00', onClick: () => navigate('/orders/inbox?status=CONFIRMED') },
    { label: 'Pending Orders Value', value: distWorth, icon: <WorthIcon />, color: '#6a1b9a', onClick: () => navigate('/orders/inbox') },
  ];

  const retailerStats = [
    { label: 'Placed Orders', value: placedOrders, icon: <OrdersIcon />, color: '#1565c0', onClick: () => navigate('/orders') },
    { label: 'Pending Invoices', value: pendingInvoices, icon: <InvoicesIcon />, color: '#ef6c00', onClick: () => navigate('/orders') },
    { label: 'Completed Orders', value: completedOrders, icon: <CompletedIcon />, color: '#2e7d32', onClick: () => navigate('/orders') },
    { label: 'Pending Orders Value', value: ordersWorth, icon: <WorthIcon />, color: '#6a1b9a', onClick: () => navigate('/orders') },
  ];

  const stats = isDistributor ? distributorStats : retailerStats;

  const worthSectionLabel  = isDistributor ? 'Revenue' : 'Orders Worth';
  const monthlyChartLabel  = isDistributor ? 'Monthly Revenue' : 'Monthly Orders Worth';
  const dailyChartLabel    = isDistributor ? 'Daily Revenue'   : 'Daily Orders Worth';

  const totalAccents = ['#1565c0', '#2e7d32', '#ef6c00', '#c62828'];
  const totalCards = [
    { label: 'Today', value: fmtAmountLabel(todayTotal), accent: totalAccents[0] },
    { label: 'Yesterday', value: fmtAmountLabel(yesterdayTotal), accent: totalAccents[1] },
    { label: 'This Week', value: fmtAmountLabel(weekTotal), accent: totalAccents[2] },
    { label: 'This Month', value: fmtAmountLabel(monthTotal), accent: totalAccents[3] },
  ];

  return (
    <Box>
      <PageHeader
        title={`Welcome back, ${user?.username ?? ''}`}
        subtitle={user ? `${user.tenantName} · ${user.roles.join(', ')}` : undefined}
      />

      {/* Stat cards — compact first row */}
      <Grid container spacing={1.5}>
        {stats.map((s) => (
          <Grid item xs={6} md={3} key={s.label}>
            <Card
              onClick={s.onClick}
              sx={{ height: CARD_HEIGHT, display: 'flex', alignItems: 'center', cursor: 'pointer', borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.06)', transition: 'box-shadow 0.15s', '&:hover': { boxShadow: 4 } }}
            >
              <CardContent sx={{ p: '10px 14px !important', width: '100%', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ p: 0.75, borderRadius: 1.5, bgcolor: s.color + '15', color: s.color, display: 'flex', flexShrink: 0 }}>
                  {s.icon}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 10, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {s.label}
                  </Typography>
                  <Typography sx={{ fontSize: 22, fontWeight: 800, color: s.color, lineHeight: 1.1, mt: 0.25 }}>
                    {s.value}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Period totals */}
      <Typography sx={{ mt: 2.5, mb: 1.5, fontSize: 13, fontWeight: 700, color: '#152346', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        {worthSectionLabel}
      </Typography>
      <Grid container spacing={1.5}>
        {totalCards.map((c) => (
          <Grid item xs={6} md={3} key={c.label}>
            <TotalCard label={c.label} value={c.value} accent={c.accent} />
          </Grid>
        ))}
      </Grid>

      {/* Charts row — same Card structure and same CHART_CSS_H keeps x-axes aligned */}
      <Grid container spacing={1.5} sx={{ mt: 0.5 }} alignItems="flex-start">
        <Grid item xs={12} md={5}>
          <Card sx={{ borderRadius: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#152346', mb: 0.25 }}>{monthlyChartLabel}</Typography>
              <Typography sx={{ fontSize: 11, color: '#9ca3af', mb: 1 }}>Last 6 months order value</Typography>
              <LineChart data={monthlyData} color="#1565c0" />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={7}>
          <Card sx={{ borderRadius: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#152346', mb: 0.25 }}>{dailyChartLabel}</Typography>
              <Typography sx={{ fontSize: 11, color: '#9ca3af', mb: 1 }}>Last 14 days</Typography>
              <LineChart data={dailyData} color="#00897b" verticalLabels />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Get started */}
      <Card sx={{ mt: 2, borderRadius: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
        <CardContent>
          <Typography variant="h3" sx={{ mb: 1 }}>Get started</Typography>
          <Typography variant="body2" color="text.secondary">
            • Add products in the Products section<br />
            • Create batches and track live stock<br />
            • Retailers can browse distributors and place orders<br />
            • Distributors process orders and generate invoices
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}
